import { apiClient } from '../utils/api-client';
import { throwForResponse } from './api-error';
import type { RouteHold } from '../types/competition-round';
import type { LeadClassStyle, LeadClimbStyle } from '../components/competition/lead-climb-style';

/** The kinds of mark on a card, in the order they can appear. */
export type AttemptType = 'attempt' | 'zone' | 'top';

export interface ScoreAttempt {
  /** Absent on an attempt the dialog has added but not yet saved. */
  id?: string;
  attempt_type: AttemptType;
}

/**
 * One person's card on one problem.
 *
 * **Two disciplines share the shape.** A boulder card is `attempts` — the goes in
 * the order they happened. A lead card is `holds_count` and `time_seconds`, and
 * its `attempts` is always empty, because a lead climber has one go. Which half to
 * read comes from the round (`round_type`), never from guessing at the fields.
 */
export interface ScoreCard {
  round_climb_id: string;
  climb_name: string | null;
  attempts: ScoreAttempt[];
  topped: boolean;
  zone?: boolean;
  /** Lead: how far up, as a judge writes it — "17+" or "17". Null for no card. */
  holds_count?: string | null;
  /** Lead: seconds on the route, thousandths in the decimals. */
  time_seconds?: number | null;
  /** Lead: the same time as a sheet reads it — `2:05.500`. */
  formatted_time?: string | null;
  /**
   * Lead: on top rope, or leading it. The **first** thing the ranking compares,
   * so a board that draws cards draws it. Null for no card.
   */
  climbed_as?: LeadClimbStyle | null;
  /** Whether a judge has to stand at this problem. */
  requires_judge?: boolean;
  /**
   * Whether the viewer may write *this* card. Per card rather than per block
   * because a team manager's permission is about whose card it is: their own
   * squad's athletes, on the problems nobody stands at.
   */
  can_score?: boolean;
  /** The stored result behind the card, when there is one. */
  result_id?: string | null;
}

/**
 * The numbers a boulder place is decided by, as the server's ranking reads a
 * card set. `judged_tops` and `judged_zones` only under a regulation that tells
 * judged blocks apart (OPES), where the attempts count on judged blocks alone.
 * A top also counts as a zone here, and in `tally`, which draws the same counts.
 */
export interface BoulderRankingScore {
  tops: number;
  zones: number;
  top_attempts: number;
  zone_attempts: number;
  judged_tops?: number;
  judged_zones?: number;
}

/** A lead place: the place on each route, and their geometric mean. */
export interface LeadRankingScore {
  places: number[];
  mean: number;
}

export type RankingScore = BoulderRankingScore | LeadRankingScore;

/**
 * Where somebody stands in their class, **worked out by the server** by the
 * competition's regulation (`RankingStrategies`) — the client draws it and
 * computes none. Null for somebody not placed yet: nothing on any card, or a
 * board narrowed to a squad or a family, which is sent no places.
 */
export interface Placement {
  rank: number | null;
  score: RankingScore | null;
  /**
   * Whether a tie-break put them in this place (`decided`) or is being climbed
   * for it (`pending`, "spareggio in corso"). Absent from older answers.
   */
  tiebreak?: 'decided' | 'pending' | null;
}

/** Which regulation a board's places come from, and whether it counts judged blocks apart. */
export interface RankingInfo {
  /** A key of `RankingStrategies`: `standard`, `opes_2026`… */
  strategy: string;
  /** Whether the boulder ranking tells judged blocks from self-certified ones. */
  judgedBlocks: boolean;
}

export interface RoundScore extends Placement {
  id: string;
  name: string | null;
  surname: string | null;
  full_name: string | null;
  team_name: string | null;
  category_id: string | null;
  category_name: string | null;
  cards: ScoreCard[];
}

export interface RoundScoreClimb {
  id: string;
  position: number;
  requires_judge: boolean;
  /**
   * Lead: the holds counted on the route — the ceiling its card's input takes.
   * Null on a boulder block, and null on a route nobody has measured, which is
   * what keeps the round from starting.
   */
  holds_count?: number | null;
  /**
   * Lead: those holds circled on the photo, numbered by where they sit in the
   * list. What the scoring dialog draws and taps to pick a hold off.
   */
  holds?: RouteHold[];
  /**
   * Lead: this route taken out of the choice of top rope or lead. Null leaves
   * the class's own setting standing — which is what `permittedLeadStyles` puts
   * together for the card.
   */
  required_climb_style?: LeadClimbStyle | null;
  /** The problem's picture: its own, or the linked climb's. */
  image_url?: string | null;
  /** What it is called on a sheet: a block's number, or the linked climb's name. */
  name: string | null;
  /** The same answer under its older name. */
  climb_name: string | null;
  climb_id: string | null;
  /** The class that climbs it. Null for a problem the whole round shares. */
  category_id: string | null;
  /**
   * Which judges a judge-only block was handed to — empty means every judge
   * — and whether this viewer may write on it.
   */
  judge_ids?: string[];
  can_score?: boolean;
}

export interface ScoreCategory {
  id: string;
  name: string;
  /**
   * How this class climbs the round, on a lead one: on top rope, leading, or
   * whichever the climber says. Absent on the "which classes does it run"
   * picker, whose classes are the competition's rather than the round's.
   */
  climb_style?: LeadClassStyle;
  /**
   * How many turns (bookable rounds) the class is split over at this
   * competition — what the tab's link to its standings across them says.
   * Absent on the "which classes does it run" picker.
   */
  turns_count?: number;
}

/** Somebody with the judge role here, offered when a block is handed out. */
export interface ScoreJudge {
  id: string;
  name: string;
}

export interface RoundScorePage {
  scores: RoundScore[];
  climbs: RoundScoreClimb[];
  /** The classes this round runs — its tabs. */
  categories: ScoreCategory[];
  /** Every class the competition offers, for the "which does it run" picker. */
  availableCategories: ScoreCategory[];
  /** The competition's judges, for handing a judge-only block to some of them. */
  judges: ScoreJudge[];
  /** The regulation the places come from. */
  ranking: RankingInfo;
}

/**
 * A card as saved, with the places of the **whole tab** it moved — everybody of
 * the class, a nil place included — so the board is in order without waiting
 * for its next poll (see `withSavedScore`). Empty on a narrowed board.
 */
export interface SavedRoundScore extends RoundScore {
  tabPlaces: Record<string, Placement>;
}

/**
 * The board after a save: the saved row as it now is, and every other row of
 * the tab with the place the server gave it. A row the save did not move keeps
 * what it had.
 */
export function withSavedScore(scores: RoundScore[], saved: RoundScore): RoundScore[] {
  const places = 'tabPlaces' in saved ? (saved as SavedRoundScore).tabPlaces : {};
  // The row is kept without them: they describe the tab at the moment of the
  // save, and would be stale on the next one.
  const row: Partial<SavedRoundScore> = { ...saved };
  delete row.tabPlaces;

  return scores.map((score) => {
    if (score.id === saved.id) return row as RoundScore;
    const place = places[score.id];
    return place ? { ...score, rank: place.rank, score: place.score } : score;
  });
}

interface RankingMeta {
  ranking_strategy?: string;
  ranking_judged_blocks?: boolean;
}

/** The regulation a response says its places come from. */
export function rankingOf(meta?: RankingMeta): RankingInfo {
  return {
    strategy: meta?.ranking_strategy ?? 'standard',
    judgedBlocks: !!meta?.ranking_judged_blocks,
  };
}

interface ScoresListResponse {
  data: { id: string; type: 'round_score'; attributes: RoundScore }[];
  meta?: RankingMeta & {
    round_climbs?: RoundScoreClimb[];
    categories?: ScoreCategory[];
    available_categories?: ScoreCategory[];
    judges?: ScoreJudge[];
  };
}

interface ScoreResponse {
  data: { id: string; type: 'round_score'; attributes: RoundScore };
  meta?: RankingMeta & { places?: Record<string, Placement> };
}

/** A boulder card as it is saved: exactly the goes it should end up with. */
export interface SavedBoulderCard {
  round_climb_id: string;
  attempts: AttemptType[];
}

/**
 * A lead card as it is saved. Both numbers or neither: an empty `holds_count`
 * takes the card off the board, which is what an empty attempt list does on a
 * boulder.
 */
export interface SavedLeadCard {
  round_climb_id: string;
  /** "17+", "17", or empty to clear the card. */
  holds_count: string;
  /** Seconds, thousandths in the decimals. Null only alongside empty holds. */
  time_seconds: number | null;
  /**
   * Top rope or lead. Sent where the class may do either — the server refuses a
   * card that does not say — and left out where only one style is open, which it
   * fills in itself. Left out on a correction that is only of the numbers: the
   * style on the card then stands.
   */
  climbed_as?: LeadClimbStyle | null;
}

export type SavedCard = SavedBoulderCard | SavedLeadCard;

/**
 * A round's scoreboard as the panel works on it.
 *
 * Saving is a **replacement**: the caller sends the attempts each named problem
 * should end up with, and the server makes it so. That is what lets the dialog
 * be edited freely and committed once — a stream of creates and deletes would
 * leave the board half-corrected if one of them failed.
 */
class RoundScoresService {
  async list(
    competitionSlug: string,
    roundId: string,
    categoryId?: string
  ): Promise<RoundScorePage> {
    const query = categoryId ? `?category_id=${encodeURIComponent(categoryId)}` : '';
    const response = await apiClient.get(
      `/api/competitions/${competitionSlug}/rounds/${roundId}/scores${query}`
    );

    if (!response.ok) {
      await throwForResponse(response, 'Failed to fetch the scoreboard');
    }

    const data: ScoresListResponse = await response.json();
    return {
      scores: data.data.map((item) => item.attributes),
      climbs: data.meta?.round_climbs ?? [],
      categories: data.meta?.categories ?? [],
      availableCategories: data.meta?.available_categories ?? [],
      judges: data.meta?.judges ?? [],
      ranking: rankingOf(data.meta),
    };
  }

  /**
   * `results` names only the problems that changed — one left out is left
   * alone, one named with an empty list (or, on a lead round, with no holds) is
   * cleared.
   *
   * A card carries whichever half its discipline uses: `attempts` on a boulder
   * round, `holds_count` and `time_seconds` on a lead one. The server reads the
   * round's own type and ignores the other.
   */
  async save(
    competitionSlug: string,
    roundId: string,
    participantId: string,
    results: SavedCard[]
  ): Promise<SavedRoundScore> {
    const response = await apiClient.put(
      `/api/competitions/${competitionSlug}/rounds/${roundId}/scores/${participantId}`,
      { results }
    );

    if (!response.ok) {
      await throwForResponse(response, 'Failed to save the scores');
    }

    const data: ScoreResponse = await response.json();
    return { ...data.data.attributes, tabPlaces: data.meta?.places ?? {} };
  }
}

export const roundScoresService = new RoundScoresService();
